import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { Modal } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import FormTextField from 'components/IAP/formFields/FormTextField';
import FormTextAreaField from 'components/IAP/formFields/FormTextAreaField';
import {
  PlannedResource,
  PlannedResourceRow,
  useUpdatePlannedResource,
} from 'components/IAP/hooks/assignmentListHooks';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';

type ResourceFormData = PlannedResource & {
  resourceLeader: string;
  resourceName: string;
};

const AssignmentCardResourceAttachmentDialog = ({
  onClose,
  data: resourceAttachmentData,
}: {
  onClose: () => void;
  data: PlannedResourceRow;
}) => {
  const { mutate: update } = useUpdatePlannedResource(
    resourceAttachmentData.id
  );

  const onSubmit = (data: ResourceFormData) => {
    update({
      id: resourceAttachmentData.id,
      numberOfPersons: resourceAttachmentData.numberOfPersons,
      contactInfo: resourceAttachmentData.contactInfo,
      reportingInfo: resourceAttachmentData.reportingInfo,
      has204a: resourceAttachmentData.has204a, // this is on purpose. We don't want to accidentally change whether this resource has a 204a form attached.
      leader: data.resourceLeader, // this is on purpose. This field is shared between two forms. We don't want to override the original.
      resourceBranch: data.resourceBranch,
      resourceDivision: data.resourceDivision,
      resourceGroup: data.resourceGroup,
      resourceStrikeTeam: data.resourceStrikeTeam,
      resourceTaskForce: data.resourceTaskForce,
      resourceAssignmentLocation: data.resourceAssignmentLocation,
      resourceSpecialInstructions: data.resourceSpecialInstructions,
      resourceSafetyPlanLocation: data.resourceSafetyPlanLocation,
    });
    onClose();
  };

  const formMethods = useForm<ResourceFormData>({
    mode: 'onChange',
    defaultValues: {
      resourceName: resourceAttachmentData.name || '', // this is on purpose. We don't want to accidentally send a new name for the resource.
      resourceLeader: resourceAttachmentData.leader || '', // this is on purpose. This field is shared between two forms. We don't want to override the original.
      resourceBranch: resourceAttachmentData.resourceBranch || '',
      resourceDivision: resourceAttachmentData.resourceDivision || '',
      resourceGroup: resourceAttachmentData.resourceGroup || '',
      resourceStrikeTeam: resourceAttachmentData.resourceStrikeTeam || '',
      resourceTaskForce: resourceAttachmentData.resourceTaskForce || '',
      resourceAssignmentLocation:
        resourceAttachmentData.resourceAssignmentLocation || '',
      resourceSpecialInstructions:
        resourceAttachmentData.resourceSpecialInstructions || '',
      resourceSafetyPlanLocation:
        resourceAttachmentData.resourceSafetyPlanLocation || '',
    },
  });

  const { handleSubmit } = formMethods;

  const { exportPlannedResourceAsForm } = useIcsFormPdfPreview();

  const handleExport = () => {
    if (resourceAttachmentData.walaId) {
      void exportPlannedResourceAsForm({
        walaId: resourceAttachmentData.walaId,
        plannedResourceId: resourceAttachmentData.id,
      });
    }
  };

  return (
    <FormProvider {...formMethods}>
      <Modal
        show={true}
        onHide={onClose}
        centered
        size="lg"
        backdrop={true}
        keyboard={false}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-3">
            <div className="d-flex justify-content-between align-items-start p-2">
              <div>
                <h4 className="m-0">Assignment List Attachment ICS-204A</h4>
                <h6 className="mt-0 text-muted fst-italic">
                  {resourceAttachmentData.name}
                </h6>
              </div>
              <StylishNewButton
                type="button"
                className="btn-sm ms-1 bg-transparent border border-neutral-500"
                onClick={() => handleExport()}
                disabled={
                  !resourceAttachmentData.has204a ||
                  !resourceAttachmentData.walaId
                }
              >
                Preview PDF of 204A Form
              </StylishNewButton>
            </div>
            <div className="d-flex justify-content-between">
              <div>
                <FormTextField title="Branch" fieldName={'resourceBranch'} />
              </div>
              <div>
                <FormTextField title="Division" fieldName="resourceDivision" />
              </div>
              <div>
                <FormTextField title="Group" fieldName="resourceGroup" />
              </div>
            </div>
            <div className="d-flex justify-content-between">
              <div>
                <FormTextField
                  title="Strike Team"
                  fieldName="resourceStrikeTeam"
                />
              </div>
              <div>
                <FormTextField
                  title="Task Force"
                  fieldName="resourceTaskForce"
                />
              </div>
              <div>
                <FormTextField
                  title="Resource"
                  fieldName="resourceName"
                  disabled
                />
              </div>
            </div>
            <div className="d-flex justify-content-between gap-2">
              <FormTextField
                variant="half"
                title="Leader"
                fieldName="resourceLeader"
              />
              <FormTextField
                variant="half"
                title="Assignment Location"
                fieldName="resourceAssignmentLocation"
              />
            </div>
            <FormTextAreaField
              title="Work Assignment Special Instructions"
              fieldName="resourceSpecialInstructions"
              rules={{
                maxLength: {
                  value: 255,
                  message: 'This maximum length is 255 characters',
                },
              }}
              variant="full"
            />
            <FormTextField
              title="Site Safety Plan Location"
              fieldName="resourceSafetyPlanLocation"
            />

            <div className="d-flex justify-content-start">
              <StylishNewButton type="submit">Save Draft</StylishNewButton>
              <StylishNewButton
                type="button"
                className="ms-1 bg-transparent border border-neutral-500"
                onClick={() => onClose()}
              >
                Close
              </StylishNewButton>
            </div>
          </div>
        </form>
      </Modal>
    </FormProvider>
  );
};

export default AssignmentCardResourceAttachmentDialog;
